"use client";

import { useRef, useState, type FC, type Ref } from "react";
import {
  Container,
  type ContainerProps,
  type ClassNameProp,
  Slides,
  type SlidesRef,
  Spacing,
  Flex,
  mergeRefs,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import { Section } from "../Section";
import type { AnalysisObservationCategory } from "../../types";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import {
  AnalysisObservationsSlide,
  type AnalysisObservationsSlideProps,
} from "./components/AnalysisObservationsSlide";
import { AnalysisObservationsButton } from "./components/AnalysisObservationsButton";
import { AnalysisObservationsModal } from "./components/AnalysisObservationsModal";
import { AnalysisObservationsHeader } from "./components/AnalysisObservationsHeader";

import {
  navContainerStyle,
  navWrapperStyle,
  sectionStyle,
  trailingButtonsStyle,
} from "./AnalysisObservations.css";

/**
 * @deprecated
 */
export type AnalysisObservationsProps = ClassNameProp & {
  /**
   * Array of observation items to display
   **/
  categories: AnalysisObservationCategory[];
  /**
   * Title for the observations section
   **/
  title?: string;
  /**
   * Description text for the observations section
   **/
  description?: string;
  /**
   * Container size
   **/
  container?: ContainerProps["size"];
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * AnalysisObservations component displays a list of observations
 * with an optional heading and description
 * @deprecated
 */
export const AnalysisObservations: FC<AnalysisObservationsProps> = ({
  categories,
  title = "Inzichten",
  container = "marketing",
  className,
  ref,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const internalRef = useContentMenuSection<HTMLDivElement>({
    id: "observations",
    label: title,
  });

  const slidesRef = useRef<SlidesRef>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!categories || categories.length === 0) {
    return null;
  }

  const totalObservations = categories.length;

  /**
   * Handle navigation to next slide with wrapping
   */
  const handleNext = () => {
    slidesRef.current?.next();
  };

  /**
   * Handle navigation to previous slide with wrapping
   */
  const handlePrevious = () => {
    slidesRef.current?.previous();
  };

  /**
   * Sync state when slide changes (for button labels)
   */
  const handleSlideChange = (slideKey: string) => {
    const index = categories.findIndex((obs) => obs.id === slideKey);
    if (index !== -1) {
      setCurrentIndex(index);
    }
  };

  // Calculate previous and next indices with wrapping
  const previousIndex =
    currentIndex === 0 ? totalObservations - 1 : currentIndex - 1;
  const nextIndex = (currentIndex + 1) % totalObservations;

  // Get the observation titles for button labels
  const previousLabel = categories[previousIndex]?.name || "";
  const nextLabel = categories[nextIndex]?.name || "";

  const currentLabel = categories[currentIndex]?.name || "";

  const handleObservationClick: AnalysisObservationsSlideProps["onObservationClick"] =
    () => {
      setIsModalOpen(true);
    };

  return (
    <>
      <Section
        ref={mergeRefs(ref, internalRef)}
        className={mergeClasses(sectionStyle, className)}
        variant="tint"
      >
        <nav className={navWrapperStyle}>
          <Container size={container} className={navContainerStyle}>
            <AnalysisObservationsHeader
              currentIndex={currentIndex}
              currentLabel={currentLabel}
              previousLabel={previousLabel}
              nextLabel={nextLabel}
              previousIndex={previousIndex}
              nextIndex={nextIndex}
              handlePrevious={handlePrevious}
              handleNext={handleNext}
              subtitle="Inzichten per onderdeel"
            />
          </Container>
        </nav>

        <Slides
          ref={slidesRef}
          slides={Object.fromEntries(
            categories.map((category) => [
              category.id,
              <AnalysisObservationsSlide
                key={category.id}
                category={category}
                container={container}
                onShowAll={() => setIsModalOpen(true)}
                onObservationClick={handleObservationClick}
              />,
            ]),
          )}
          onSlideChange={handleSlideChange}
        />

        <Container size={container} className={trailingButtonsStyle}>
          <Spacing />
          <Flex direction="row" gap="s" justify="space-between">
            <AnalysisObservationsButton
              label={previousLabel}
              variant="previous"
              onClick={handlePrevious}
              index={previousIndex}
            />
            <AnalysisObservationsButton
              label={nextLabel}
              variant="next"
              onClick={handleNext}
              index={nextIndex}
            />
          </Flex>
        </Container>

        <Spacing height="s" />
      </Section>

      {isModalOpen && (
        <AnalysisObservationsModal
          categories={categories}
          onClose={() => setIsModalOpen(false)}
          defaultIndex={currentIndex}
        />
      )}
    </>
  );
};
